import React, { useEffect, useState, useRef, useCallback, useContext } from 'react';
import { Link, Prompt } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagic, faSave, faTrash, faEdit, faCheck, faPaperPlane, faInfoCircle, faCopy, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import { useSelector } from 'react-redux';

import Button from '../../UI/Button/Button';
import { EInputType, IInputField } from '../../UI/Input/Input';
import { useInputs } from '../../UI/Input/useInputs';
import InputGroup from '../../UI/InputGroup/InputGroup';
import ModalContext, { EModalSize } from '../../UI/Modal/ModalContext';
import AiOfferModal from '../AiOfferModal/AiOfferModal';
import { IOffer, IOfferNote, IAppState, IAuthState, IOfferRequest, IOfferRequestState } from '../../../interfaces';
import OfferService from '../../../services/OfferService';
import OfferNoteService from '../../../services/OfferNoteService';
import Spinner from '../../UI/Spinner/Spinner';
import { getInputData, initForm } from '../../../shared/utility';

interface IProps {
  offerRequestId: string;
  offerRequest?: IOfferRequest;
}

export enum EOffer {
  state = "state",
  priceWithoutVat = "priceWithoutVat",
  priceVatP = "priceVatP",
  priceWithVat = "priceWithVat",
  priceVat = "priceVat",
  message = "message",
  notes = "notes",
}

const OfferEdit: React.FC<IProps> = ({ offerRequestId, offerRequest }) => {
  const { setModal, closeModal } = useContext(ModalContext);
  const { offerRequest: reduxOfferRequest } = useSelector<IAppState, IOfferRequestState>((state) => state.offerRequest);
  const currentOfferRequest = offerRequest || reduxOfferRequest;

  const [offer, setOffer] = useState<IOffer | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [generatingAi, setGeneratingAi] = useState<boolean>(false);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [initialDataStr, setInitialDataStr] = useState<string>("");
  const [previousMessage, setPreviousMessage] = useState<string | null>(null);
  const [archived, setArchived] = useState<boolean>(false);
  const [copySuccess, setCopySuccess] = useState<boolean>(false);

  const { authUser } = useSelector<IAppState, IAuthState>((state) => state.auth);

  const [notesList, setNotesList] = useState<IOfferNote[]>([]);
  const [notesLoading, setNotesLoading] = useState<boolean>(false);
  const [newNoteText, setNewNoteText] = useState<string>("");
  const [isComposerFocused, setIsComposerFocused] = useState<boolean>(false);
  const [savingNote, setSavingNote] = useState<boolean>(false);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingNoteText, setEditingNoteText] = useState<string>("");
  const [savingEdit, setSavingEdit] = useState<boolean>(false);
  const [deletingNoteId, setDeletingNoteId] = useState<string | null>(null);
  const [deletingNote, setDeletingNote] = useState<boolean>(false);

  const formatRelativeTime = (dateStr?: string): string => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffSec = Math.floor(diffMs / 1000);
      const diffMin = Math.floor(diffSec / 60);
      const diffHr = Math.floor(diffMin / 60);
      const diffDays = Math.floor(diffHr / 24);

      if (diffSec < 60) {
        return 'juuri nyt';
      } else if (diffMin < 60) {
        return `${diffMin} min sitten`;
      } else if (diffHr < 24) {
        return `${diffHr} t sitten`;
      } else if (diffDays === 1) {
        return 'eilen';
      } else if (diffDays < 7) {
        return `${diffDays} pv sitten`;
      } else {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}.${month}.${year}`;
      }
    } catch (e) {
      return '';
    }
  };

  const getInitials = (name?: string): string => {
    if (!name) return 'U';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const fetchNotes = useCallback(async () => {
    if (!offer?.id) return;
    try {
      setNotesLoading(true);
      const notes = await OfferNoteService.getNotes(offer.id);
      if (isMountedRef.current) {
        setNotesList(notes);
      }
    } catch (err) {
      console.error("Error fetching notes:", err);
    } finally {
      if (isMountedRef.current) {
        setNotesLoading(false);
      }
    }
  }, [offer?.id]);

  useEffect(() => {
    if (offer?.id) {
      fetchNotes();
    }
  }, [offer?.id, fetchNotes]);

  const handleAddNote = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newNoteText.trim() || !offer?.id) return;
    try {
      setSavingNote(true);
      const savedNote = await OfferNoteService.saveNote(offer.id, newNoteText);
      if (isMountedRef.current) {
        setNotesList(prev => [savedNote, ...prev]);
        setNewNoteText("");
        setIsComposerFocused(false);
      }
    } catch (err) {
      console.error("Error adding note:", err);
    } finally {
      if (isMountedRef.current) {
        setSavingNote(false);
      }
    }
  };

  const handleEditNote = async (id: string, text: string) => {
    if (!text.trim()) return;
    try {
      setSavingEdit(true);
      const updated = await OfferNoteService.updateNote(id, text);
      if (isMountedRef.current) {
        setNotesList(prev => prev.map(note => note.id === id ? updated : note));
        setEditingNoteId(null);
        setEditingNoteText("");
      }
    } catch (err) {
      console.error("Error editing note:", err);
    } finally {
      if (isMountedRef.current) {
        setSavingEdit(false);
      }
    }
  };

  const handleDeleteNote = async (id: string) => {
    try {
      setDeletingNote(true);
      await OfferNoteService.deleteNote(id);
      if (isMountedRef.current) {
        setNotesList(prev => prev.filter(note => note.id !== id));
        setDeletingNoteId(null);
      }
    } catch (err) {
      console.error("Error deleting note:", err);
    } finally {
      if (isMountedRef.current) {
        setDeletingNote(false);
      }
    }
  };

  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const [inputs, setInputs] = useState<IInputField>({
    [EOffer.state]: {
      type: EInputType.reactSelect,
      label: "Tila",
      value: "DRAFT",
      options: [
        { value: "DRAFT", label: "Luonnos" },
        { value: "SENT", label: "Lähetetty" },
        { value: "WON", label: "Voitettu" },
        { value: "LOST", label: "Ei tilannut" },
      ],
    },
    [EOffer.priceWithoutVat]: {
      type: EInputType.number,
      label: "Hinta (alv 0%)",
      value: "",
    },
    [EOffer.priceVatP]: {
      type: EInputType.number,
      label: "Alv %",
      value: "25.5",
    },
    [EOffer.priceWithVat]: {
      type: EInputType.number,
      label: "Hinta (sis. alv)",
      value: "",
    },
    [EOffer.message]: {
      type: EInputType.textarea,
      label: "",
      value: "",
      rows: 12,
    },
    [EOffer.notes]: {
      type: EInputType.textarea,
      label: "",
      value: "",
      rows: 12,
    },
  });

  const { createInput } = useInputs({
    inputs,
    setInputs
  });

  useEffect(() => {
    const fetchOffer = async () => {
      try {
        setLoading(true);
        const offers = await OfferService.getOffers(offerRequestId);
        if (!isMountedRef.current) return;
        if (offers && offers.length > 0) {
          const currentOffer = offers[0];
          setOffer(currentOffer);
          setArchived(currentOffer.archived || false);
          initForm(setInputs, {
            ...currentOffer,
            state: currentOffer.state || "DRAFT",
            priceVatP: currentOffer.priceVatP || 25.5
          });
        }
      } catch (err) {
        console.error(err);
      } finally {
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    };
    if (offerRequestId) {
      fetchOffer();
    }
  }, [offerRequestId]);

  useEffect(() => {
    const getInitDataStr = () => {
      return JSON.stringify({
        state: offer?.state || "DRAFT",
        priceWithoutVat: offer?.priceWithoutVat?.toString() || "",
        priceVatP: offer?.priceVatP?.toString() || "25.5",
        priceWithVat: offer?.priceWithVat?.toString() || "",
        message: offer?.message || "",
        notes: offer?.notes || "",
        archived: offer?.archived || false
      });
    };
    setInitialDataStr(getInitDataStr());
  }, [offer]);

  useEffect(() => {
    if (loading) return;
    const currentData = {
      state: inputs[EOffer.state].value || "",
      priceWithoutVat: inputs[EOffer.priceWithoutVat].value?.toString() || "",
      priceVatP: inputs[EOffer.priceVatP].value?.toString() || "",
      priceWithVat: inputs[EOffer.priceWithVat].value?.toString() || "",
      message: inputs[EOffer.message].value || "",
      notes: inputs[EOffer.notes].value || "",
      archived: archived
    };
    setIsDirty(JSON.stringify(currentData) !== initialDataStr);
  }, [inputs, archived, initialDataStr, loading]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  const saveHandler = async (e?: React.MouseEvent) => {
    if (e) e.preventDefault();
    try {
      setSaving(true);
      setError(null);
      setSuccessMsg(null);
      
      const offerData = getInputData<IOffer>(inputs);
      offerData.offerRequestId = offerRequestId;
      offerData.archived = archived;
      
      // Auto-calculate VAT
      const priceW = parseFloat(offerData.priceWithVat as any) || 0;
      const vatP = parseFloat(offerData.priceVatP as any) || 25.5;
      if (priceW > 0) {
        offerData.priceWithoutVat = Number((priceW / (1 + vatP / 100)).toFixed(2));
        offerData.priceVat = Number((priceW - offerData.priceWithoutVat).toFixed(2));
      }
      
      // Auto-transition to SENT if price > 0 and state is DRAFT or unset
      if ((offerData.state === "DRAFT" || !offerData.state) && (priceW > 0 || (offerData.priceWithoutVat && offerData.priceWithoutVat > 0))) {
        offerData.state = "SENT";
      }
      
      let savedOffer;
      if (offer?.id) {
        savedOffer = await OfferService.updateOffer(offer.id, offerData);
      } else {
        savedOffer = await OfferService.saveOffer(offerData);
      }
      if (!isMountedRef.current) return;
      setOffer(savedOffer);
      initForm(setInputs, savedOffer);
      setSuccessMsg("Tarjous tallennettu onnistuneesti");
      setTimeout(() => {
        if (isMountedRef.current) {
          setSuccessMsg(null);
        }
      }, 3000);
    } catch (err) {
      if (isMountedRef.current) {
        setError("Virhe tarjousta tallennettaessa");
      }
      console.error(err);
    } finally {
      if (isMountedRef.current) {
        setSaving(false);
      }
    }
  };

  const generateAiDraftDirectly = async (targetInputs: IInputField) => {
    try {
      setGeneratingAi(true);
      setError(null);
      
      const currentMessage = targetInputs[EOffer.message].value as string;
      setPreviousMessage(currentMessage);
      
      const offerData = getInputData<IOffer>(targetInputs);
      const priceW = parseFloat(offerData.priceWithVat as any) || 0;
      const vatP = parseFloat(offerData.priceVatP as any) || 25.5;
      if (priceW > 0) {
        offerData.priceWithoutVat = Number((priceW / (1 + vatP / 100)).toFixed(2));
        offerData.priceVat = Number((priceW - offerData.priceWithoutVat).toFixed(2));
      }
      
      const draft = await OfferService.generateAiDraft(offerRequestId, offerData);
      if (!isMountedRef.current) return;
      if (draft) {
        setInputs(prev => ({
          ...prev,
          [EOffer.message]: {
            ...prev[EOffer.message],
            value: draft
          }
        }));
        setSuccessMsg("AI-luonnos generoitu");
        setTimeout(() => {
          if (isMountedRef.current) {
            setSuccessMsg(null);
          }
        }, 3000);
      } else {
        setPreviousMessage(null);
      }
    } catch (err) {
      console.error(err);
      if (isMountedRef.current) {
        setError("Virhe tekoälyvastauksen luonnissa");
        setPreviousMessage(null);
      }
    } finally {
      if (isMountedRef.current) {
        setGeneratingAi(false);
      }
    }
  };

  const handleGenerateAi = async (e: React.MouseEvent) => {
    e.preventDefault();
    await generateAiDraftDirectly(inputs);
  };

  const handleOpenAiModal = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!currentOfferRequest) return;
    setModal({
      isOpen: true,
      title: `AI-tarjous ja esikatselu: ${currentOfferRequest.service?.name || ''}`,
      size: EModalSize.FULL,
      content: (
        <AiOfferModal
          offerRequest={currentOfferRequest}
          initialOffer={offer}
          onClose={closeModal}
          onSaved={(savedOffer) => {
            setOffer(savedOffer);
            initForm(setInputs, savedOffer);
            setIsDirty(false);
            closeModal();
          }}
          onApplyToOffer={(applied) => {
            setInputs(prev => {
              const updated = { ...prev };
              if (applied.message !== undefined) {
                updated[EOffer.message] = {
                  ...updated[EOffer.message],
                  value: applied.message
                };
              }
              if (applied.priceWithVat !== undefined) {
                updated[EOffer.priceWithVat] = {
                  ...updated[EOffer.priceWithVat],
                  value: String(applied.priceWithVat)
                };
              }
              if (applied.priceVatP !== undefined) {
                updated[EOffer.priceVatP] = {
                  ...updated[EOffer.priceVatP],
                  value: String(applied.priceVatP)
                };
              }
              if (applied.priceWithoutVat !== undefined) {
                updated[EOffer.priceWithoutVat] = {
                  ...updated[EOffer.priceWithoutVat],
                  value: String(applied.priceWithoutVat)
                };
              }
              return updated;
            });
            setIsDirty(true);
            closeModal();
          }}
        />
      )
    });
  };

  const handleUndo = () => {
    if (previousMessage !== null) {
      setInputs(prev => ({
        ...prev,
        [EOffer.message]: {
          ...prev[EOffer.message],
          value: previousMessage
        }
      }));
      setPreviousMessage(null);
      setSuccessMsg("Teksti palautettu");
      setTimeout(() => {
        if (isMountedRef.current) {
          setSuccessMsg(null);
        }
      }, 3000);
    }
  };

  const handleCopyToClipboard = () => {
    const text = (inputs[EOffer.message]?.value as string) || '';
    if (!text) return;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(() => {
        setCopySuccess(true);
        setTimeout(() => {
          if (isMountedRef.current) setCopySuccess(false);
        }, 2500);
      }).catch(() => {
        setCopySuccess(false);
      });
    } else {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand("copy");
        setCopySuccess(true);
        setTimeout(() => {
          if (isMountedRef.current) setCopySuccess(false);
        }, 2500);
      } catch (e) {
        setCopySuccess(false);
      }
      document.body.removeChild(textArea);
    }
  };

  const handleOpenMailto = () => {
    const email = currentOfferRequest?.email || '';
    if (!email) return;
    const serviceName = currentOfferRequest?.service?.name || 'Tarjous';
    const city = currentOfferRequest?.city ? ` - ${currentOfferRequest.city}` : '';
    const subject = `Tarjous: ${serviceName}${city}`;
    const body = (inputs[EOffer.message]?.value as string) || '';
    const mailtoUrl = `mailto:${encodeURIComponent(email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoUrl;
  };

  const updatePriceWO = () => {
    setInputs(prev => {
      const priceW = parseFloat(prev[EOffer.priceWithVat].value as any) || 0;
      const vatP = parseFloat(prev[EOffer.priceVatP].value as any) || 25.5;
      let priceWO = 0;
      if (priceW > 0) {
        priceWO = priceW / (1 + vatP / 100);
      }
      return {
        ...prev,
        [EOffer.priceWithoutVat]: { 
          ...prev[EOffer.priceWithoutVat], 
          value: priceWO > 0 ? parseFloat(priceWO.toFixed(2)) : "" 
        }
      };
    });
  };

  const handlePriceWithVatBlur = async (value: any) => {
    updatePriceWO();
    
    const priceW = parseFloat(value as any) || 0;
    const isDraft = inputs[EOffer.state]?.value === "DRAFT" || !inputs[EOffer.state]?.value;
    if (priceW > 0 && isDraft) {
      setInputs(prev => ({
        ...prev,
        [EOffer.state]: {
          ...prev[EOffer.state],
          value: "SENT"
        }
      }));
    }

    const currentMessage = inputs[EOffer.message].value as string;
    if (!currentMessage || currentMessage.trim() === '') {
      if (priceW > 0) {
        const latestInputs = {
          ...inputs,
          [EOffer.priceWithVat]: {
            ...inputs[EOffer.priceWithVat],
            value: value
          },
          ...(isDraft ? {
            [EOffer.state]: {
              ...inputs[EOffer.state],
              value: "SENT"
            }
          } : {})
        };
        await generateAiDraftDirectly(latestInputs);
      }
    }
  };

  const renderStateSelector = () => {
    const stateInput = inputs[EOffer.state];
    return (
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#475569', marginBottom: '0.5rem' }}>
          {stateInput.label}:
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
          <div style={{ display: 'inline-flex', backgroundColor: '#f1f5f9', padding: '4px', borderRadius: '8px', border: '1px solid #e2e8f0', gap: '4px' }}>
            {stateInput.options?.map((opt, index) => {
              const isSelected = stateInput.value === opt.value;
              let activeBg = '#e2e8f0';
              let activeColor = '#0f172a';
              if (isSelected) {
                if (opt.value === 'WON') {
                  activeBg = '#dcfce7';
                  activeColor = '#16a34a';
                } else if (opt.value === 'LOST') {
                  activeBg = '#fee2e2';
                  activeColor = '#dc2626';
                } else if (opt.value === 'SENT') {
                  activeBg = '#dbeafe';
                  activeColor = '#2563eb';
                } else {
                  activeBg = '#ffffff';
                  activeColor = '#0f172a';
                }
              }
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setInputs(prev => ({
                      ...prev,
                      [EOffer.state]: { ...prev[EOffer.state], value: opt.value }
                    }));
                  }}
                  style={{
                    border: 'none',
                    outline: 'none',
                    cursor: 'pointer',
                    padding: '6px 16px',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    fontWeight: isSelected ? 600 : 500,
                    transition: 'all 0.2s ease',
                    backgroundColor: isSelected ? activeBg : 'transparent',
                    color: isSelected ? activeColor : '#64748b',
                    boxShadow: isSelected && opt.value === 'DRAFT' ? '0 1px 2px 0 rgba(0, 0, 0, 0.05)' : 'none',
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.color = '#0f172a';
                      e.currentTarget.style.backgroundColor = '#e2e8f0';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.color = '#64748b';
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  {index}. {opt.label}
                </button>
              );
            })}
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 600, color: '#475569', userSelect: 'none' }}>
            <input
              type="checkbox"
              checked={archived}
              onChange={(e) => setArchived(e.target.checked)}
              style={{
                width: '1.1rem',
                height: '1.1rem',
                cursor: 'pointer'
              }}
            />
            <span>Ei tarjota (piilota listoilta)</span>
          </label>
        </div>

        <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
          {stateInput.value === 'SENT' && (
            <span>
              <strong style={{ color: '#1d4ed8' }}>Lähetetty:</strong> Tarjous on toimitettu asiakkaalle. <em>(Huom: HankiX ei lähetä sähköpostia puolestasi automaattisesti)</em>
            </span>
          )}
          {stateInput.value === 'DRAFT' && (
            <span><strong>Luonnos:</strong> Tarjousta valmistellaan sisäisesti, ei vielä toimitettu asiakkaalle.</span>
          )}
          {stateInput.value === 'WON' && (
            <span><strong style={{ color: '#16a34a' }}>Voitettu:</strong> Asiakas on hyväksynyt tarjouksen ja toimeksianto on sovittu.</span>
          )}
          {stateInput.value === 'LOST' && (
            <span><strong style={{ color: '#dc2626' }}>Ei tilannut:</strong> Asiakas ei valinnut tarjousta tai tarjouspyyntö on rauennut.</span>
          )}
        </div>
      </div>
    );
  };

  if (loading) return <Spinner />;

  return (
    <div style={{ backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e2e8f0', padding: '1.5rem', marginTop: '1.5rem' }}>
      <style>{`
        @media (max-width: 991px) {
          .offer-edit-grid {
            grid-template-columns: 1fr !important;
            gap: 1.5rem !important;
          }
          .offer-edit-header-row {
            flex-direction: column !important;
            align-items: flex-start !important;
            height: auto !important;
            margin-bottom: 1rem !important;
            gap: 0.75rem !important;
          }
          .offer-edit-buttons {
            width: 100% !important;
            justify-content: flex-start !important;
            flex-wrap: wrap !important;
          }
        }
      `}</style>
      <Prompt
        when={isDirty}
        message={() => "Sinulla on tallentamattomia muutoksia. Haluatko varmasti poistua sivulta?"}
      />
      <h3 style={{ marginTop: 0, marginBottom: '1.5rem', fontSize: '1.2rem', color: '#0f172a', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <span>Tarjous</span>
          {isDirty && (
            <span style={{ fontSize: '0.8rem', fontWeight: 600, backgroundColor: '#fef3c7', color: '#d97706', padding: '2px 8px', borderRadius: '12px', border: '1px solid #fde68a' }}>
              Tallentamattomia muutoksia
            </span>
          )}
        </div>
      </h3>
      
      {error && <div style={{ color: 'red', marginBottom: '1rem' }}>{error}</div>}
      {successMsg && <div style={{ color: 'green', marginBottom: '1rem' }}>{successMsg}</div>}

      {/* Yläosa (Koko leveys): Tila ja Hinnat */}
      <div style={{ marginBottom: '2rem' }}>
        {renderStateSelector()}
        <div style={{ marginTop: '1rem', maxWidth: '800px' }}>
          <InputGroup>
            {createInput(EOffer.priceWithVat, { onBlur: handlePriceWithVatBlur })}
            {createInput(EOffer.priceVatP, { onBlur: updatePriceWO })}
            {createInput(EOffer.priceWithoutVat, { disabled: true })}
          </InputGroup>
        </div>
      </div>

      {/* Alaosa (Kaksipalstainen ruudukko): Viesti ja Muistiinpanot */}
      <div className="offer-edit-grid" style={{ display: 'grid', gridTemplateColumns: '2.4fr 1fr', gap: '2.5rem', maxWidth: '1200px' }}>
        {/* Vasen sarake: Viesti asiakkaalle */}
        <div>
          <div className="offer-edit-header-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '38px' }}>
            <label style={{ fontWeight: 600, color: '#334155', margin: 0 }}>Viesti asiakkaalle</label>
            <div className="offer-edit-buttons" style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              {previousMessage !== null && (
                <Button onClick={handleUndo} style={{ backgroundColor: '#f1f5f9', color: '#334155', border: '1px solid #cbd5e1', borderRadius: '4px', padding: '6px 12px', fontSize: '0.85rem' }}>
                  Kumoa
                </Button>
              )}
              <Button onClick={handleGenerateAi} disabled={generatingAi} style={{ backgroundColor: '#1d4ed8', color: 'white', border: 'none', borderRadius: '4px', padding: '6px 12px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem' }}>
                <FontAwesomeIcon icon={faMagic} /> {generatingAi ? 'Luodaan...' : 'AI-vastaus'}
              </Button>
              {currentOfferRequest && (
                <Button 
                  onClick={handleOpenAiModal} 
                  title="Muokkaa palvelun AI-ohjetta ja esikatsele" 
                  data-testid="offer-edit-ai-modal-btn"
                  style={{ backgroundColor: '#f1f5f9', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: '4px', padding: '6px 10px', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem' }}
                >
                  <FontAwesomeIcon icon={faEdit} />
                </Button>
              )}
              <Button 
                onClick={saveHandler} 
                disabled={saving} 
                style={{ 
                  padding: '6px 12px', 
                  fontSize: '0.85rem', 
                  fontWeight: 600,
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  backgroundColor: isDirty ? '#f97316' : undefined,
                  borderColor: isDirty ? '#ea580c' : undefined,
                  boxShadow: isDirty ? '0 4px 6px -1px rgba(249, 115, 22, 0.15), 0 2px 4px -2px rgba(249, 115, 22, 0.15)' : 'none',
                  color: isDirty ? '#ffffff' : undefined
                }}
              >
                <FontAwesomeIcon icon={faSave} /> {saving ? 'Tallennetaan...' : 'Tallenna tarjous'}
              </Button>
            </div>
          </div>
          <div style={{
            backgroundColor: '#f8fafc',
            border: '1px solid #cbd5e1',
            borderRadius: '6px',
            padding: '10px 14px',
            marginTop: '0.5rem',
            marginBottom: '0.75rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px'
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
              <FontAwesomeIcon icon={faInfoCircle} style={{ color: '#2563eb', marginTop: '2px', flexShrink: 0 }} />
              <div style={{ fontSize: '0.825rem', color: '#1e3a8a', lineHeight: '1.45' }}>
                <strong>Huomio: HankiX ei lähetä sähköpostia puolestasi.</strong> Toimita tarjous asiakkaalle kopioimalla viesti tai avaamalla suoraan sähköpostiohjelmaasi. AI-viestin muotoa ja palvelukohtaisia ohjeita voit muokata <Link to="/organizations/me#ai-tarjous" style={{ color: '#1d4ed8', fontWeight: 600, textDecoration: 'underline' }}>[AI-asetuksissa]</Link>.
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginLeft: '22px' }}>
              <button
                type="button"
                onClick={handleCopyToClipboard}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '5px 10px',
                  fontSize: '0.8rem',
                  fontWeight: 500,
                  backgroundColor: copySuccess ? '#dcfce7' : '#ffffff',
                  color: copySuccess ? '#15803d' : '#1e40af',
                  border: copySuccess ? '1px solid #86efac' : '1px solid #93c5fd',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                <FontAwesomeIcon icon={copySuccess ? faCheck : faCopy} />
                {copySuccess ? 'Kopioitu leikepöydälle!' : 'Kopioi leikepöydälle'}
              </button>

              <button
                type="button"
                onClick={handleOpenMailto}
                disabled={!currentOfferRequest?.email}
                title={!currentOfferRequest?.email ? 'Asiakkaalle ei ole määritetty sähköpostiosoitetta' : 'Avaa sähköpostiohjelma'}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '5px 10px',
                  fontSize: '0.8rem',
                  fontWeight: 500,
                  backgroundColor: '#ffffff',
                  color: !currentOfferRequest?.email ? '#94a3b8' : '#1e40af',
                  border: !currentOfferRequest?.email ? '1px solid #cbd5e1' : '1px solid #93c5fd',
                  borderRadius: '4px',
                  cursor: !currentOfferRequest?.email ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                <FontAwesomeIcon icon={faEnvelope} />
                Avaa sähköpostissa (mailto)
              </button>
            </div>
          </div>
          {createInput(EOffer.message)}
          
          {isDirty && (
            <div style={{ fontSize: '0.85rem', color: '#d97706', display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 500, marginTop: '1rem' }}>
              <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#d97706', display: 'inline-block' }}></span>
              Muistathan tallentaa tekemäsi muutokset!
            </div>
          )}
        </div>

        {/* Oikea sarake: Sisäiset muistiinpanot */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', height: '38px' }}>
            <label style={{ fontWeight: 600, color: '#334155', margin: 0 }}>Sisäiset muistiinpanot</label>
          </div>
          <div style={{ fontSize: '0.825rem', color: '#64748b', marginTop: '0.25rem', marginBottom: '1.25rem', lineHeight: '1.4' }}>
            Näkyy kaikille oman yrityksesi käyttäjille, ei asiakkaalle
          </div>

          {!offer?.id ? (
            <div style={{ padding: '1.5rem', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px dashed #cbd5e1', textAlign: 'center', color: '#64748b' }}>
              <p style={{ margin: 0, fontWeight: 500, fontSize: '0.9rem' }}>
                Tallenna tarjous ensin, jotta voit lisätä ja tarkastella sisäisiä muistiinpanoja.
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              {/* Uuden muistiinpanon lisäyslaatikko (Composer) */}
              <form onSubmit={handleAddNote} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', position: 'relative' }}>
                <textarea
                  value={newNoteText}
                  onChange={(e) => setNewNoteText(e.target.value)}
                  onFocus={() => setIsComposerFocused(true)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      if (newNoteText.trim() && !savingNote) {
                        handleAddNote(e);
                      }
                    }
                  }}
                  placeholder="Kirjoita uusi muistiinpano... (Paina Enter tallentaaksesi, Shift+Enter rivinvaihtoon)"
                  style={{
                    width: '100%',
                    minHeight: isComposerFocused ? '100px' : '42px',
                    maxHeight: '200px',
                    padding: '10px 14px',
                    borderRadius: '8px',
                    border: isComposerFocused ? '1px solid #f97316' : '1px solid #e2e8f0',
                    outline: 'none',
                    fontSize: '0.9rem',
                    color: '#334155',
                    backgroundColor: '#ffffff',
                    boxShadow: isComposerFocused ? '0 0 0 2px rgba(249, 115, 22, 0.15)' : 'none',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    resize: isComposerFocused ? 'vertical' : 'none',
                    lineHeight: '1.5',
                  }}
                />
                {isComposerFocused && (
                  <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                      <Button
                        type="submit"
                        onClick={() => {}}
                        disabled={savingNote || !newNoteText.trim()}
                        style={{
                          padding: '6px 14px',
                          fontSize: '0.85rem',
                          fontWeight: 600,
                          backgroundColor: '#f97316',
                          borderColor: '#ea580c',
                          color: 'white',
                          borderRadius: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                        }}
                      >
                        <FontAwesomeIcon icon={faPaperPlane} size="sm" />
                        {savingNote ? 'Tallennetaan...' : 'Tallenna'}
                      </Button>
                      <button
                        type="button"
                        onClick={() => {
                          setNewNoteText("");
                          setIsComposerFocused(false);
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#64748b',
                          fontSize: '0.85rem',
                          fontWeight: 500,
                          cursor: 'pointer',
                          padding: '4px 8px',
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.color = '#334155'}
                        onMouseLeave={(e) => e.currentTarget.style.color = '#64748b'}
                      >
                        Peruuta
                      </button>
                    </div>
                    <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Enter tallentaa · Shift+Enter rivinvaihto</span>
                  </div>
                )}
              </form>

              {/* Muistiinpanoluettelo (Timeline UI) */}
              {notesLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '1rem' }}>
                  <Spinner />
                </div>
              ) : notesList.length === 0 ? (
                <div style={{ padding: '2rem 1rem', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px dashed #e2e8f0', textAlign: 'center', color: '#94a3b8', fontSize: '0.875rem' }}>
                  Ei sisäisiä muistiinpanoja vielä.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', position: 'relative', paddingLeft: '8px' }}>
                  {/* Pystysuora aikajanalinja */}
                  <div
                    style={{
                      position: 'absolute',
                      left: '23px',
                      top: '20px',
                      bottom: '20px',
                      width: '2px',
                      backgroundImage: 'linear-gradient(to bottom, #e2e8f0 50%, rgba(255,255,255,0) 0%)',
                      backgroundPosition: 'right',
                      backgroundSize: '1px 8px',
                      backgroundRepeat: 'repeat-y',
                      zIndex: 1,
                    }}
                  />

                  {notesList.map((note) => {
                    const isAuthor = authUser && note.userId === authUser.id.toString();
                    const isPrimary = authUser && authUser.isPrimary;
                    const canEditOrDelete = isAuthor || isPrimary;
                    const initials = getInitials(note.userName);
                    const isEditing = editingNoteId === note.id;

                    return (
                      <div key={note.id} style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', position: 'relative', zIndex: 2 }}>
                        {/* Avatar-pallero */}
                        <div
                          style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            background: isAuthor ? 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)' : 'linear-gradient(135deg, #64748b 0%, #475569 100%)',
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.75rem',
                            fontWeight: 700,
                            flexShrink: 0,
                            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                          }}
                        >
                          {initials}
                        </div>

                        {/* Kortti/Sisältö */}
                        <div
                          style={{
                            flexGrow: 1,
                            backgroundColor: '#f8fafc',
                            borderRadius: '8px',
                            border: '1px solid #e2e8f0',
                            padding: '10px 14px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '0.35rem',
                            transition: 'all 0.2s ease',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = '#cbd5e1';
                            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.02)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = '#e2e8f0';
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                        >
                          {/* Yläotsikko (Nimi, aika ja toiminnot) */}
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                              <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#334155' }}>
                                {note.userName || 'Käyttäjä'}
                              </span>
                              <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                                {formatRelativeTime(note.created)}
                              </span>
                            </div>

                            {/* Toiminnot (Muokkaa/Poista) */}
                            {canEditOrDelete && !isEditing && (
                              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setEditingNoteId(note.id);
                                    setEditingNoteText(note.content);
                                  }}
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    padding: '2px 4px',
                                    cursor: 'pointer',
                                    color: '#94a3b8',
                                    fontSize: '0.8rem',
                                    transition: 'color 0.15s ease',
                                  }}
                                  title="Muokkaa muistiinpanoa"
                                  onMouseEnter={(e) => e.currentTarget.style.color = '#f97316'}
                                  onMouseLeave={(e) => e.currentTarget.style.color = '#94a3b8'}
                                >
                                  <FontAwesomeIcon icon={faEdit} size="sm" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setDeletingNoteId(note.id)}
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    padding: '2px 4px',
                                    cursor: 'pointer',
                                    color: '#94a3b8',
                                    fontSize: '0.8rem',
                                    transition: 'color 0.15s ease',
                                  }}
                                  title="Poista muistiinpano"
                                  onMouseEnter={(e) => e.currentTarget.style.color = '#ef4444'}
                                  onMouseLeave={(e) => e.currentTarget.style.color = '#94a3b8'}
                                >
                                  <FontAwesomeIcon icon={faTrash} size="sm" />
                                </button>
                              </div>
                            )}
                          </div>

                          {/* Sisältöosa */}
                          {isEditing ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.25rem' }}>
                              <textarea
                                value={editingNoteText}
                                onChange={(e) => setEditingNoteText(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" && !e.shiftKey) {
                                    e.preventDefault();
                                    if (editingNoteText.trim() && !savingEdit) {
                                      handleEditNote(note.id, editingNoteText);
                                    }
                                  }
                                }}
                                style={{
                                  width: '100%',
                                  minHeight: '60px',
                                  padding: '8px 12px',
                                  borderRadius: '6px',
                                  border: '1px solid #f97316',
                                  fontSize: '0.875rem',
                                  color: '#334155',
                                  outline: 'none',
                                }}
                              />
                              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                <button
                                  type="button"
                                  disabled={savingEdit || !editingNoteText.trim()}
                                  onClick={() => handleEditNote(note.id, editingNoteText)}
                                  style={{
                                    backgroundColor: '#f97316',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    padding: '4px 10px',
                                    fontSize: '0.8rem',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                  }}
                                >
                                  <FontAwesomeIcon icon={faCheck} size="xs" />
                                  {savingEdit ? 'Tallennetaan...' : 'Tallenna'}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setEditingNoteId(null);
                                    setEditingNoteText("");
                                  }}
                                  style={{
                                    backgroundColor: 'transparent',
                                    color: '#64748b',
                                    border: 'none',
                                    borderRadius: '4px',
                                    padding: '4px 10px',
                                    fontSize: '0.8rem',
                                    fontWeight: 500,
                                    cursor: 'pointer',
                                  }}
                                >
                                  Peruuta
                                </button>
                              </div>
                            </div>
                          ) : deletingNoteId === note.id ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.25rem', padding: '8px', backgroundColor: '#fef2f2', borderRadius: '6px', border: '1px solid #fecaca' }}>
                              <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#991b1b' }}>
                                Haluatko varmasti poistaa tämän muistiinpanon?
                              </span>
                              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                <button
                                  type="button"
                                  disabled={deletingNote}
                                  onClick={() => handleDeleteNote(note.id)}
                                  style={{
                                    backgroundColor: '#ef4444',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    padding: '4px 10px',
                                    fontSize: '0.8rem',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                  }}
                                >
                                  {deletingNote ? 'Poistetaan...' : 'Kyllä, poista'}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setDeletingNoteId(null)}
                                  style={{
                                    backgroundColor: 'transparent',
                                    color: '#64748b',
                                    border: 'none',
                                    borderRadius: '4px',
                                    padding: '4px 10px',
                                    fontSize: '0.8rem',
                                    fontWeight: 500,
                                    cursor: 'pointer',
                                  }}
                                >
                                  Peruuta
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div style={{ fontSize: '0.875rem', color: '#475569', whiteSpace: 'pre-line', lineHeight: '1.6' }}>
                              {note.content}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OfferEdit;
